const SERVER_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:2309";
const AUTH_TOKEN_KEY = "uno_auth_token";
const AUTH_USER_KEY = "uno_auth_user";

export interface AuthUser {
  id: string;
  username: string;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

class AuthService {
  /**
   * Registers a new user
   */
  async register(username: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${SERVER_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Registration failed");
    }

    const data: AuthResponse = await response.json();
    this.saveAuth(data);
    return data;
  }

  /**
   * Logs in a user
   */
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${SERVER_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Login failed");
    }

    const data: AuthResponse = await response.json();
    this.saveAuth(data);
    return data;
  }

  /**
   * Logs out the current user
   */
  async logout(): Promise<void> {
    const token = this.getAuthToken();
    if (token) {
      try {
        await fetch(`${SERVER_URL}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ token }),
        });
      } catch (error) {
        console.error("[AuthService] Logout error:", error);
      }
    }
    this.clearAuth();
  }

  /**
   * Gets the current auth token
   */
  getAuthToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  /**
   * Gets the current authenticated user
   */
  getAuthUser(): AuthUser | null {
    try {
      const userData = localStorage.getItem(AUTH_USER_KEY);
      if (!userData) {
        return null;
      }
      return JSON.parse(userData) as AuthUser;
    } catch (error) {
      console.error("[AuthService] Error reading auth user:", error);
      return null;
    }
  }

  /**
   * Checks if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  /**
   * Saves auth data to localStorage
   */
  private saveAuth(data: AuthResponse): void {
    localStorage.setItem(AUTH_TOKEN_KEY, data.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
  }

  /**
   * Clears auth data from localStorage
   */
  private clearAuth(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
  }

  /**
   * Validates token with server
   */
  async validateToken(): Promise<boolean> {
    const token = this.getAuthToken();
    if (!token) {
      return false;
    }

    try {
      const response = await fetch(`${SERVER_URL}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        this.clearAuth();
        return false;
      }

      const data = await response.json();
      // Update user data if changed
      if (data.user) {
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
      }
      return true;
    } catch (error) {
      console.error("[AuthService] Token validation error:", error);
      this.clearAuth();
      return false;
    }
  }
}

export const authService = new AuthService();
